import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useVariation } from "@hackler/react-sdk";
import {
  customEvent,
  HACKLE_DEVTOOLS_STORAGE_KEY,
  HACKLE_VARIATIONS,
  registerExperiment,
  SafeGTMProperties,
  useIsDevEnvironment,
} from "goi_common";

// ===== TEMP DIAGNOSTIC: A/B 노출 유실 구간 판별용 (실험 311/315 한정, 진단 후 제거) =====
const EXPOSURE_DEBUG_URL =
  "https://script.google.com/macros/s/AKfycbzU-T0brv19ZbfCOTCqlEHg8XThvNtttWr96FsCm7ATQyouhRMv327rtOARZmjyAz9UVQ/exec";
const EXPOSURE_DEBUG_KEYS = [311, 315];

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

function logExposureDebug(
  phase: "render" | "push",
  experimentKey: number,
  variation: string,
  exposureId: string,
) {
  try {
    if (typeof window === "undefined" || typeof fetch !== "function") return;
    if (!EXPOSURE_DEBUG_KEYS.includes(experimentKey)) return;
    const ga = document.cookie.match(/_ga=GA\d+\.\d+\.(\d+\.\d+)/);
    const body = JSON.stringify({
      exposure_id: exposureId,
      experiment_key: experimentKey,
      variation,
      phase,
      route: window.location.pathname,
      anon_id: ga ? ga[1] : "",
    });
    // sendBeacon은 Apps Script의 302 리다이렉트를 안 따라가 doPost 미실행 → fetch(keepalive)로 전송
    fetch(EXPOSURE_DEBUG_URL, { method: "POST", body, keepalive: true, mode: "no-cors" });
  } catch {
    // 진단 로그 실패는 무시 - 퍼널/실험에 영향 0
  }
}
// ===== /TEMP DIAGNOSTIC =====

type VariationMap = Record<number, string>;

export const useCustomVariation = (
  experimentKey: number,
  defaultVariation?: string,
  contextExtra?: SafeGTMProperties,
): string => {
  const variation = useVariation(experimentKey, defaultVariation || "Z");

  const isDevEnvironment = useIsDevEnvironment();
  const [clientVariation, setClientVariation] = useState(defaultVariation || "Z");

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (isDevEnvironment === true) {
      registerExperiment(experimentKey);

      // localStorage 우선 체크
      const stored = window.localStorage.getItem(HACKLE_DEVTOOLS_STORAGE_KEY);
      if (stored) {
        try {
          const variations: VariationMap = JSON.parse(stored);
          const testVariation = variations[experimentKey];

          if (HACKLE_VARIATIONS.includes(testVariation as any)) {
            setClientVariation(testVariation);
            return; // 찾았으면 early return
          }
        } catch {
          // ignore
        }
      }

      // fallback to server variation
      setClientVariation(variation);
    }
  }, [experimentKey, isDevEnvironment, variation]);

  const resolvedVariation = isDevEnvironment === true ? clientVariation : variation;

  const exposedKeyRef = useRef<string>("");
  const debugIdRef = useRef<string>("");

  // TEMP DIAGNOSTIC: render(paint 이전) 시점 노출 기록 (311/315)
  useIsomorphicLayoutEffect(() => {
    if (resolvedVariation === "Z") return;
    if (debugIdRef.current) return;
    debugIdRef.current =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    logExposureDebug("render", experimentKey, resolvedVariation, debugIdRef.current);
  }, [resolvedVariation, experimentKey]);

  useEffect(() => {
    if (resolvedVariation === "Z") return;

    const experimentVariation = `${experimentKey}-${resolvedVariation}`;
    if (exposedKeyRef.current === experimentVariation) return;
    exposedKeyRef.current = experimentVariation;

    // TEMP DIAGNOSTIC: dataLayer.push 직전 시점 노출 기록 (311/315)
    logExposureDebug("push", experimentKey, resolvedVariation, debugIdRef.current);

    customEvent({
      newGtm: {
        action: "experiment_exposure",
        properties: Object.assign(
          { experiment_variation: experimentVariation } as SafeGTMProperties,
          contextExtra,
        ),
      },
    });
  }, [resolvedVariation, experimentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return resolvedVariation;
};
